import {
  deleteReviewReply,
  draftReviewReply,
  fetchGuestReviews,
  fetchPortalProfile,
  fetchReviewAlerts,
  fetchReviewSplit,
  moderateReview,
  publishReviewReply,
  restoreReview,
  savePortalProfile,
  saveReviewAlerts,
  saveReviewSplit
} from '@api/taptotip';
import TopNav from '@components/Nav/TopNav';
import TipDirectLeftMenu from '@components/Nav/TopNav/TipDirectLeftMenu';
import { StyleBreakpoints } from '@constants';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import EmptyStar from '../../../assets/images/EmptyStar.svg';
import Star from '../../../assets/images/Star.svg';

// Card 288 — guest review management. PROTOTYPE.
//
// Two operator tools over our own first-party guest reviews: a public reply,
// and moderation. Both are scoped to `tap_to_tip_review` and that is permanent,
// not a first phase: Viator and Tripadvisor review text is closed-tier under
// their sync rules and Tripadvisor has no reply API, so there is nothing on a
// third-party review for this screen to act on.
//
// The whole tab is gated on isGuestReviewsEnabled (master prototype switch plus
// a per-company opt-in). It is deliberately separate from the existing Reviews
// tab, which ships to real customers today and is visible to guides: a guide
// must not be able to hide a review about themselves.

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',

    [theme.breakpoints.up(StyleBreakpoints.sm)]: {
      flexDirection: 'row'
    }
  }
}));

const CARD = 'w-full bg-white rounded-3xl shadow-sm border border-solid border-slate-200 p-6 min-w-0';

/**
 * Mirrors resolveModerationStatus on the server. This copy exists only to
 * PREVIEW the consequence to the operator before they commit. The server
 * derives the real status from the reason it is sent and never trusts a status
 * from the client, so the two cannot drift into a security problem, only into a
 * misleading label.
 */
const MODERATION_REASONS = [
  {
    value: 'abusive',
    label: 'Abusive or harassing',
    detail: 'Slurs, threats, or an attack on a person rather than the tour.'
  },
  {
    value: 'personal_data',
    label: 'Contains personal details',
    detail: 'A phone number, an address, a booking reference, or a full name.'
  },
  {
    value: 'spam',
    label: 'Spam or advertising',
    detail: 'Promotional text, link farming, or obvious bot content.'
  },
  {
    value: 'off_topic',
    label: 'Not about this tour',
    detail: 'The guest has reviewed a different product or a different company.'
  },
  {
    value: 'disputed',
    label: 'We disagree with this review',
    detail: 'The review is genuine but you do not want it shown publicly.'
  }
];

const removesFromRating = reason => reason !== 'disputed';

const FILTERS = [
  { value: 'needsReply', label: 'Needs a reply' },
  { value: 'replied', label: 'Replied' },
  { value: 'moderated', label: 'Hidden or removed' },
  { value: 'all', label: 'All' }
];

const MAX_REPLY_LENGTH = 1200;

const formatDate = value =>
  new Date(value).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });

const Stars = ({ value }) => (
  <div className="flex items-center" aria-label={`${value} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map(n => (
      <img key={n} src={n <= value ? Star : EmptyStar} alt="" className="w-4 h-auto mr-0.5" />
    ))}
  </div>
);

Stars.propTypes = { value: PropTypes.number.isRequired };

const Pill = ({ tone, children }) => {
  const tones = {
    slate: 'bg-slate-100 text-slate-600 border-slate-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    rose: 'bg-rose-50 text-rose-700 border-rose-200',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200'
  };

  return (
    <span
      className={`inline-block text-[10px] font-bold px-2 py-1 rounded-lg border border-solid uppercase tracking-wider ${tones[tone]}`}>
      {children}
    </span>
  );
};

Pill.propTypes = { tone: PropTypes.string.isRequired, children: PropTypes.node };

// Andy's bounds (2026-09-22). The floor is 1 in 50 rather than zero: this is a
// dial for how many guests we keep, not a switch for whether the feature runs.
const SPLIT_MIN = 2;
const SPLIT_MAX = 50;

const describeSplit = pct =>
  pct >= 50 ? '1 in 2 guests' : `1 in ${Math.round(100 / pct)} guests`;

/**
 * How many guests write their review with us rather than being sent to
 * Tripadvisor. The field behind this has existed for a long time and TA-Tipping
 * already rolls against it; this is the first time an operator can see or
 * change it.
 */
const ReviewSplit = ({ split, onSave, busy, error }) => {
  const [value, setValue] = useState(split.percentage);
  const dirty = value !== split.percentage || !split.enabled;

  useEffect(() => setValue(split.percentage), [split.percentage]);

  return (
    <section className={`${CARD} mb-6`}>
      <h2 className="text-base font-black text-slate-900 m-0">Where your reviews go</h2>
      <p className="text-sm text-slate-500 leading-relaxed m-0 mt-1 mb-5 max-w-[68ch]">
        When a happy guest is ready to review you, we can either send them to Tripadvisor or keep
        them here and collect the review ourselves. Reviews we collect are yours: you can reply to
        them, take them down, and publish them anywhere.
      </p>

      <div className="flex items-baseline gap-3">
        <span className="text-3xl font-black text-slate-900">{describeSplit(value)}</span>
        <span className="text-sm text-slate-500">stay with us. The rest go to Tripadvisor.</span>
      </div>

      <label className="block mt-4">
        <span className="sr-only">Share of guests kept on TripAdmit</span>
        <input
          type="range"
          min={SPLIT_MIN}
          max={SPLIT_MAX}
          step={1}
          value={value}
          onChange={e => setValue(Number(e.target.value))}
          className="w-full max-w-lg accent-indigo-600 cursor-pointer"
          aria-valuetext={`${describeSplit(value)} kept on TripAdmit`}
        />
        <span className="flex justify-between max-w-lg text-xs text-slate-400 font-semibold mt-1">
          <span>1 in 50</span>
          <span>1 in 2</span>
        </span>
      </label>

      {!split.enabled && (
        <p className="text-xs text-slate-500 leading-relaxed m-0 mt-3 max-w-[68ch]">
          You have not set this yet, so every guest currently goes to Tripadvisor. Saving turns it
          on.
        </p>
      )}

      {error && (
        <p className="text-sm text-rose-700 bg-rose-50 border border-solid border-rose-200 rounded-xl p-3 m-0 mt-4">
          {error}
        </p>
      )}

      <button
        type="button"
        disabled={busy || !dirty}
        onClick={() => onSave(value)}
        className="mt-4 bg-indigo-600 hover:bg-indigo-700 active:translate-y-px disabled:opacity-60 text-white text-sm font-bold border-0 rounded-xl px-4 py-2.5 cursor-pointer transition-colors">
        {busy ? 'Saving' : split.enabled ? 'Save' : 'Turn on and save'}
      </button>
    </section>
  );
};

ReviewSplit.propTypes = {
  split: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  busy: PropTypes.bool,
  error: PropTypes.string
};

/**
 * The WordPress widget setup guide.
 *
 * The plugin needs four things and an operator can only work out one of them on
 * their own. This turns "email support and wait" into "here is your company id
 * and the exact shortcode, you need one key from us".
 *
 * The API host is us.tip.direct for EVERY operator, EU included, and that is
 * not a bug. TipDirectAPI runs only on the US box, but the data is not
 * regionally partitioned: the EU backend's mongo WRITE connection points at the
 * US cluster and eu-west-3 is a read replica. One database, one feed host.
 */
const WIDGET_API_URL = 'https://us.tip.direct/tipdirectapi';
const WIDGET_SHORTCODE = '[tipdirect_reviews layout="grid" per_page="6"]';

const CopyField = ({ label, value, hint }) => {
  const [state, setState] = useState('idle'); // idle | copied | selected
  const codeRef = React.useRef(null);

  /**
   * `navigator.clipboard.writeText` is not as reliable as it looks: it is
   * absent on any non-secure origin and older Safari, and it REJECTS when the
   * document is not focused. Doing nothing on failure leaves a button that
   * looks broken, so we always fall back to selecting the text, which at least
   * makes the operator's own copy shortcut one keystroke away.
   */
  const selectText = () => {
    if (!codeRef.current) return;
    window.getSelection()?.selectAllChildren(codeRef.current);
    setState('selected');
    setTimeout(() => setState('idle'), 2500);
  };

  const copy = () => {
    const done = () => {
      setState('copied');
      setTimeout(() => setState('idle'), 1600);
    };

    if (!navigator.clipboard?.writeText) return selectText();
    navigator.clipboard.writeText(value).then(done, selectText);
  };

  return (
    <div className="mt-4">
      <span className="block text-xs font-bold text-slate-700">{label}</span>
      {hint && <span className="block text-xs text-slate-500 mt-0.5">{hint}</span>}
      <div className="flex gap-2 mt-1.5">
        <code
          ref={codeRef}
          onClick={selectText}
          className="flex-1 min-w-0 text-xs text-slate-800 bg-slate-50 border border-solid border-slate-200 rounded-xl px-3 py-2.5 overflow-x-auto whitespace-nowrap cursor-text">
          {value}
        </code>
        <button
          type="button"
          onClick={copy}
          className="shrink-0 bg-white hover:bg-slate-50 text-slate-700 text-xs font-bold border border-solid border-slate-300 rounded-xl px-3 py-2.5 cursor-pointer transition-colors">
          {state === 'copied' ? 'Copied' : state === 'selected' ? 'Press Ctrl C' : 'Copy'}
        </button>
      </div>
    </div>
  );
};

CopyField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  hint: PropTypes.string
};

const Step = ({ n, title, children }) => (
  <div className="flex gap-4">
    <span className="shrink-0 w-7 h-7 rounded-full bg-indigo-50 text-indigo-700 text-sm font-black flex items-center justify-center">
      {n}
    </span>
    <div className="min-w-0 flex-1 pb-7">
      <div className="text-sm font-bold text-slate-900">{title}</div>
      {children}
    </div>
  </div>
);

Step.propTypes = {
  n: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node
};

const WordPressWidget = ({ companyId }) => {
  const [open, setOpen] = useState(false);

  return (
    <section className={`${CARD} mb-6`}>
      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-black text-slate-900 m-0">Show these reviews on your website</h2>
          <p className="text-sm text-slate-500 leading-relaxed m-0 mt-1 max-w-[68ch]">
            Our WordPress plugin puts your guest reviews on your own site. It writes them straight
            into the page rather than loading them with JavaScript, which means Google and the AI
            assistants can actually read them. Most review widgets cannot say that.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="shrink-0 bg-white hover:bg-slate-50 text-slate-700 text-sm font-bold border border-solid border-slate-300 rounded-xl px-4 py-2.5 cursor-pointer transition-colors">
          {open ? 'Hide' : 'Set it up'}
        </button>
      </div>

      {open && (
        <div className="mt-6 pt-6 border-0 border-t border-solid border-slate-200">
          <Step n={1} title="Get the plugin">
            <p className="text-sm text-slate-500 leading-relaxed m-0 mt-1 max-w-[68ch]">
              It is not on the WordPress plugin directory yet, so ask us for the file and we will
              send it over. In WordPress go to Plugins, Add New, Upload Plugin, then activate it.
            </p>
          </Step>

          <Step n={2} title="Connect it to your account">
            <p className="text-sm text-slate-500 leading-relaxed m-0 mt-1 max-w-[68ch]">
              In WordPress open TripAdmit Reviews, then Settings. Leave the environment on
              Production and paste these two in.
            </p>
            <CopyField label="API Base URL" value={WIDGET_API_URL} />
            <CopyField label="Company ID" value={companyId} hint="Yours. Nobody else has this one." />
            <p className="text-sm text-slate-500 leading-relaxed m-0 mt-4 max-w-[68ch]">
              You also need an API key, and that one we have to issue by hand. Ask us and we will
              set it up.
            </p>
          </Step>

          <Step n={3} title="Put it on a page">
            <p className="text-sm text-slate-500 leading-relaxed m-0 mt-1 max-w-[68ch]">
              Paste this shortcode wherever you want the reviews to appear.
            </p>
            <CopyField label="Shortcode" value={WIDGET_SHORTCODE} />
            <p className="text-sm text-slate-500 leading-relaxed m-0 mt-4 max-w-[68ch]">
              Change <code className="text-xs">layout</code> to <code className="text-xs">list</code>,{' '}
              <code className="text-xs">carousel</code> or <code className="text-xs">badge</code> to
              suit the page, or to <code className="text-xs">badge-inline</code>,{' '}
              <code className="text-xs">mini</code> or <code className="text-xs">micro</code> for a
              one-line rating that fits beside a Book button. <code className="text-xs">per_page</code> sets how many show at once, and{' '}
              <code className="text-xs">min_rating="5"</code> shows only five-star cards on that
              widget (your rating still counts every review).
            </p>
            <p className="text-sm text-slate-500 leading-relaxed m-0 mt-2 max-w-[68ch]">
              For a tour page, the plugin lists each of your tours with its own ready-made
              shortcode under TripAdmit Reviews → Manage Reviews → Your Tours in WordPress. Every
              layout and option is explained on the plugin&apos;s Settings page.
            </p>
          </Step>

          <div className="bg-slate-50 border border-solid border-slate-200 rounded-2xl p-4">
            <div className="text-sm font-bold text-slate-900">What appears on your site</div>
            <p className="text-sm text-slate-500 leading-relaxed m-0 mt-1 max-w-[68ch]">
              Reviews your guests wrote, with their star ratings. Guests who left stars without
              writing anything still count toward your average and the rating breakdown, but they do
              not appear as cards, because an empty card helps nobody. Anything you hide or remove on
              this page stops being sent to your site.
            </p>
          </div>
        </div>
      )}
    </section>
  );
};

WordPressWidget.propTypes = { companyId: PropTypes.string.isRequired };

/**
 * Bad review alerts.
 *
 * The email itself is not new: a guest who rates below your review threshold is
 * shown a private feedback form, and submitting it already emails you. What was
 * missing was any say over it. So this defaults to ON, unlike everything else
 * on this screen, because switching it off has to be a choice somebody made.
 */
const ReviewAlerts = ({ alerts, onSave, busy, error }) => {
  const [enabled, setEnabled] = useState(alerts.enabled);
  const [threshold, setThreshold] = useState(alerts.threshold);
  const [cadence, setCadence] = useState(alerts.cadence);
  const [email, setEmail] = useState(alerts.email || '');

  useEffect(() => {
    setEnabled(alerts.enabled);
    setThreshold(alerts.threshold);
    setCadence(alerts.cadence);
    setEmail(alerts.email || '');
  }, [alerts]);

  const dirty =
    enabled !== alerts.enabled ||
    threshold !== alerts.threshold ||
    cadence !== alerts.cadence ||
    email !== (alerts.email || '');

  return (
    <section className={`${CARD} mb-6`}>
      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-black text-slate-900 m-0">
            Tell me about unhappy guests
          </h2>
          <p className="text-sm text-slate-500 leading-relaxed m-0 mt-1 max-w-[68ch]">
            When a guest rates you poorly we show them a private feedback form instead of sending
            them to Tripadvisor. If they fill it in, we can email you so you can put it right while
            they still remember the tour.
          </p>
        </div>
        <label className="shrink-0 flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={e => setEnabled(e.target.checked)}
            className="w-4 h-4 accent-indigo-600 cursor-pointer"
          />
          <span className="text-sm font-bold text-slate-700">{enabled ? 'On' : 'Off'}</span>
        </label>
      </div>

      {enabled && (
        <div className="mt-6 pt-6 border-0 border-t border-solid border-slate-200">
          <span className="block text-sm font-bold text-slate-900">Tell me about</span>
          <div className="flex flex-wrap gap-2 mt-2">
            {[1, 2, 3, 4, 5].map(n => (
              <button
                key={n}
                type="button"
                onClick={() => setThreshold(n)}
                className={`text-sm font-bold rounded-xl px-3.5 py-2 cursor-pointer border border-solid transition-colors ${
                  threshold === n
                    ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                }`}>
                {n} {n === 1 ? 'star' : 'stars'} or fewer
              </button>
            ))}
          </div>

          <span className="block text-sm font-bold text-slate-900 mt-6">How often</span>
          <div className="flex flex-wrap gap-2 mt-2">
            {[
              { value: 'immediate', label: 'As they come in' },
              { value: 'daily', label: 'Once a day' }
            ].map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => setCadence(option.value)}
                className={`text-sm font-bold rounded-xl px-3.5 py-2 cursor-pointer border border-solid transition-colors ${
                  cadence === option.value
                    ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                }`}>
                {option.label}
              </button>
            ))}
          </div>

          <label className="block mt-6">
            <span className="block text-sm font-bold text-slate-900">Send them to</span>
            <span className="block text-xs text-slate-500 mt-0.5 mb-2">
              {alerts.fallbackEmail
                ? `Leave this empty and we will use ${alerts.fallbackEmail}.`
                : 'You have no review address set, so fill this in or the alerts have nowhere to go.'}
            </span>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder={alerts.fallbackEmail || 'you@yourcompany.com'}
              className="w-full max-w-md text-sm text-slate-900 placeholder-slate-400 border border-solid border-slate-300 rounded-xl px-3 py-2.5 focus:outline-none focus:border-indigo-500"
            />
          </label>
        </div>
      )}

      {error && (
        <p className="text-sm text-rose-700 bg-rose-50 border border-solid border-rose-200 rounded-xl p-3 m-0 mt-4">
          {error}
        </p>
      )}

      <button
        type="button"
        disabled={busy || !dirty}
        onClick={() => onSave({ enabled, threshold, cadence, email })}
        className="mt-6 bg-indigo-600 hover:bg-indigo-700 active:translate-y-px disabled:opacity-60 text-white text-sm font-bold border-0 rounded-xl px-4 py-2.5 cursor-pointer transition-colors">
        {busy ? 'Saving' : 'Save'}
      </button>
    </section>
  );
};

ReviewAlerts.propTypes = {
  alerts: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  busy: PropTypes.bool,
  error: PropTypes.string
};

/**
 * Filters over the review list.
 *
 * Entirely client-side, and that is a decision rather than a shortcut: the list
 * already arrives whole (the cap is 5,000 and the busiest operator in the
 * estate holds 492), so filtering in the browser is instant, needs no endpoint,
 * and cannot disagree with the counts above it.
 *
 * What earned a place, and why:
 *
 *  - STARS. The operator's main lens is "show me the bad ones". Multi-select
 *    rather than a threshold, because "1s and 5s" is a real question when you
 *    are looking for what changed.
 *  - WRITTEN ONLY. About three quarters of what we collect is a rating with no
 *    text. Someone scanning for something to act on does not want those, and
 *    this is the one filter that meaningfully shrinks the list.
 *  - TOUR and GUIDE. Rendered only when the operator actually has more than
 *    one, otherwise they are controls that can only ever do nothing.
 *  - SEARCH. Covers review text and guest name together. Cheap, and catches
 *    everything the structured filters do not.
 *
 * Deliberately NOT here: a date range. It needs a picker, and newest-first
 * ordering plus search covers the cases an operator actually hits. Worth adding
 * the day someone asks for "last quarter".
 */
const ALL_STARS = [1, 2, 3, 4, 5];

const matchesFilters = (review, f) => {
  if (f.stars.length && !f.stars.includes(review.star)) return false;
  if (f.writtenOnly && !(review.reviewText || review.privateFeedback)?.trim()) return false;
  if (f.tour && review.tourTitle !== f.tour) return false;
  if (f.guide && review.guideName !== f.guide) return false;

  if (f.search.trim()) {
    const needle = f.search.trim().toLowerCase();
    const haystack = [review.reviewText, review.privateFeedback, review.customerName]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    if (!haystack.includes(needle)) return false;
  }

  return true;
};

const ReviewFilters = ({ filters, setFilters, tours, guides, shown, total }) => {
  const patch = next => setFilters(prev => ({ ...prev, ...next }));

  const toggleStar = n =>
    patch({
      stars: filters.stars.includes(n)
        ? filters.stars.filter(s => s !== n)
        : [...filters.stars, n]
    });

  const active =
    filters.stars.length > 0 ||
    filters.writtenOnly ||
    Boolean(filters.tour) ||
    Boolean(filters.guide) ||
    Boolean(filters.search.trim());

  const selectClass =
    'text-sm text-slate-700 bg-white border border-solid border-slate-200 rounded-xl px-3 py-2 cursor-pointer focus:outline-none focus:border-indigo-500';

  return (
    <div className="mb-5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider mr-1">
          Stars
        </span>
        {ALL_STARS.map(n => (
          <button
            key={n}
            type="button"
            aria-pressed={filters.stars.includes(n)}
            onClick={() => toggleStar(n)}
            className={`text-sm font-bold rounded-xl px-3 py-2 cursor-pointer border border-solid transition-colors ${
              filters.stars.includes(n)
                ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
            }`}>
            {n}
          </button>
        ))}

        <button
          type="button"
          aria-pressed={filters.writtenOnly}
          onClick={() => patch({ writtenOnly: !filters.writtenOnly })}
          className={`text-sm font-bold rounded-xl px-3.5 py-2 cursor-pointer border border-solid transition-colors ${
            filters.writtenOnly
              ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
              : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
          }`}>
          Written reviews only
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-2">
        <input
          type="search"
          value={filters.search}
          onChange={e => patch({ search: e.target.value })}
          placeholder="Search reviews and guest names"
          className="flex-1 min-w-[220px] max-w-sm text-sm text-slate-900 placeholder-slate-400 border border-solid border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:border-indigo-500"
        />

        {/* Only offered when there is more than one to choose between. A
            dropdown with a single option is a control that cannot do anything. */}
        {tours.length > 1 && (
          <select
            value={filters.tour}
            onChange={e => patch({ tour: e.target.value })}
            className={selectClass}>
            <option value="">All tours</option>
            {tours.map(t => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        )}

        {guides.length > 1 && (
          <select
            value={filters.guide}
            onChange={e => patch({ guide: e.target.value })}
            className={selectClass}>
            <option value="">All guides</option>
            {guides.map(g => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        )}

        <span className="text-xs text-slate-500 font-semibold ml-auto">
          {shown === total ? `${total} shown` : `${shown} of ${total} shown`}
        </span>

        {active && (
          <button
            type="button"
            onClick={() =>
              setFilters({ stars: [], writtenOnly: false, tour: '', guide: '', search: '' })
            }
            className="text-sm font-bold text-indigo-700 hover:text-indigo-800 bg-transparent border-0 cursor-pointer px-2 py-2">
            Clear
          </button>
        )}
      </div>
    </div>
  );
};

ReviewFilters.propTypes = {
  filters: PropTypes.object.isRequired,
  setFilters: PropTypes.func.isRequired,
  tours: PropTypes.array.isRequired,
  guides: PropTypes.array.isRequired,
  shown: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired
};

const MAX_PORTAL_DESCRIPTION = 600;

/**
 * The operator's public profile on the review portal.
 *
 * Publication is OPT-OUT (Andy, 2026-09-23): a profile exists because the
 * operator collects reviews, and they can withdraw it. That choice is what
 * makes the copy here matter more than the controls. An operator who has not
 * noticed their reviews are public has not opted out of anything, so the URL is
 * stated plainly at the top with a working link rather than parked behind a
 * settings row nobody opens.
 */
const PortalProfile = ({ profile, onSave, busy, error }) => {
  const [published, setPublished] = useState(profile.published);
  const [description, setDescription] = useState(profile.description || '');
  const [bookingUrl, setBookingUrl] = useState(profile.bookingUrl || '');

  useEffect(() => {
    setPublished(profile.published);
    setDescription(profile.description || '');
    setBookingUrl(profile.bookingUrl || '');
  }, [profile]);

  const tooLong = description.length > MAX_PORTAL_DESCRIPTION;
  const dirty =
    published !== profile.published ||
    description !== (profile.description || '') ||
    bookingUrl !== (profile.bookingUrl || '');

  return (
    <section className={`${CARD} mb-6`}>
      <h2 className="text-base font-black text-slate-900 m-0">Your public review page</h2>

      {profile.published ? (
        <>
          <p className="text-sm text-slate-500 leading-relaxed m-0 mt-1 max-w-[68ch]">
            Your reviews are published on a public page that search engines and AI assistants can
            read. This is live now.
          </p>
          <a
            href={profile.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-3 text-sm font-bold text-indigo-700 hover:text-indigo-800 break-all">
            {profile.url}
          </a>
        </>
      ) : (
        <p className="text-sm text-slate-500 leading-relaxed m-0 mt-1 max-w-[68ch]">
          Your public page is switched off. Nothing about {profile.companyName} appears on the review
          portal, and any links to it stop working.
        </p>
      )}

      <div className="mt-6 pt-6 border-0 border-t border-solid border-slate-200">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={published}
            onChange={e => setPublished(e.target.checked)}
            className="w-4 h-4 mt-0.5 accent-indigo-600 cursor-pointer"
          />
          <span className="min-w-0">
            <span className="block text-sm font-bold text-slate-900">
              Show my reviews on the public page
            </span>
            <span className="block text-xs text-slate-500 leading-relaxed mt-0.5 max-w-[68ch]">
              Turn this off and your page disappears, along with every link to an individual review.
              Your reviews stay here in your dashboard either way.
            </span>
          </span>
        </label>

        {published && (
          <>
            <label className="block mt-6">
              <span className="block text-sm font-bold text-slate-900">About your tours</span>
              <span className="block text-xs text-slate-500 mt-0.5 mb-2 max-w-[68ch]">
                Your own words, shown at the top of the page. Everything else on it is either a plain
                fact or something we worked out from your reviews, so this is the only part that is
                yours to write.
              </span>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={3}
                placeholder={`What ${profile.companyName || 'your company'} does, and what makes a tour with you worth booking.`}
                className={`w-full max-w-2xl text-sm text-slate-900 placeholder-slate-400 border border-solid rounded-xl p-3 focus:outline-none ${
                  tooLong ? 'border-rose-400 focus:border-rose-500' : 'border-slate-300 focus:border-indigo-500'
                }`}
              />
              <span
                className={`block text-xs font-semibold mt-1 ${tooLong ? 'text-rose-600' : 'text-slate-400'}`}>
                {description.length} / {MAX_PORTAL_DESCRIPTION}
              </span>
            </label>

            <label className="block mt-5">
              <span className="block text-sm font-bold text-slate-900">Where people book you</span>
              <span className="block text-xs text-slate-500 mt-0.5 mb-2 max-w-[68ch]">
                Your own booking page. We list it above every other way to book, including ours,
                because a booking you take direct costs you no commission.
              </span>
              <input
                type="url"
                value={bookingUrl}
                onChange={e => setBookingUrl(e.target.value)}
                placeholder="https://yourcompany.com/tours"
                className="w-full max-w-md text-sm text-slate-900 placeholder-slate-400 border border-solid border-slate-300 rounded-xl px-3 py-2.5 focus:outline-none focus:border-indigo-500"
              />
            </label>
          </>
        )}
      </div>

      {error && (
        <p className="text-sm text-rose-700 bg-rose-50 border border-solid border-rose-200 rounded-xl p-3 m-0 mt-4">
          {error}
        </p>
      )}

      <button
        type="button"
        disabled={busy || !dirty || tooLong}
        onClick={() => onSave({ published, description, bookingUrl })}
        className="mt-6 bg-indigo-600 hover:bg-indigo-700 active:translate-y-px disabled:opacity-60 text-white text-sm font-bold border-0 rounded-xl px-4 py-2.5 cursor-pointer transition-colors">
        {busy ? 'Saving' : 'Save'}
      </button>
    </section>
  );
};

PortalProfile.propTypes = {
  profile: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  busy: PropTypes.bool,
  error: PropTypes.string
};

const LoadingSkeleton = () => (
  // Shaped like the queue it replaces so the page does not jump.
  <div className="flex flex-col gap-4 w-full" aria-label="Loading reviews" role="status">
    {[0, 1, 2].map(i => (
      <div key={i} className={`${CARD} animate-pulse`}>
        <div className="h-3 w-24 bg-slate-100 rounded" />
        <div className="h-4 w-1/2 bg-slate-100 rounded mt-3" />
        <div className="h-3 w-full bg-slate-100 rounded mt-4" />
        <div className="h-3 w-4/6 bg-slate-100 rounded mt-2" />
      </div>
    ))}
  </div>
);

const ModerationDialog = ({ review, onCancel, onConfirm, busy }) => {
  const [reason, setReason] = useState('disputed');
  const [note, setNote] = useState('');

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Hide or remove this review">
      <div className="bg-white rounded-3xl shadow-lg border border-solid border-slate-200 p-6 w-full max-w-lg max-h-[90dvh] overflow-y-auto">
        <h2 className="text-lg font-black text-slate-900 m-0">Hide or remove this review</h2>
        <p className="text-sm text-slate-500 leading-relaxed m-0 mt-1">
          Pick the reason that fits. The reason decides whether the review keeps counting toward your
          rating.
        </p>

        <div className="flex flex-col gap-2 mt-5">
          {MODERATION_REASONS.map(option => (
            <label
              key={option.value}
              className={`flex gap-3 p-3 rounded-xl border border-solid cursor-pointer transition-colors ${
                reason === option.value
                  ? 'border-indigo-300 bg-indigo-50/50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}>
              <input
                type="radio"
                name="moderation-reason"
                className="mt-1"
                checked={reason === option.value}
                onChange={() => setReason(option.value)}
              />
              <span className="min-w-0">
                <span className="block text-sm font-bold text-slate-900">{option.label}</span>
                <span className="block text-xs text-slate-500 leading-relaxed mt-0.5">
                  {option.detail}
                </span>
              </span>
            </label>
          ))}
        </div>

        {/* The consequence, stated plainly before they commit. This is the one
            thing on the screen an operator must not be able to miss. */}
        <div
          className={`mt-5 p-4 rounded-xl border border-solid ${
            removesFromRating(reason)
              ? 'bg-rose-50 border-rose-200'
              : 'bg-amber-50 border-amber-200'
          }`}>
          <div
            className={`text-sm font-bold ${
              removesFromRating(reason) ? 'text-rose-800' : 'text-amber-900'
            }`}>
            {removesFromRating(reason)
              ? 'This review will be removed and will stop counting toward your rating.'
              : 'This review will be hidden, but it will still count toward your rating.'}
          </div>
          <p
            className={`text-xs leading-relaxed m-0 mt-1 ${
              removesFromRating(reason) ? 'text-rose-700' : 'text-amber-800'
            }`}>
            {removesFromRating(reason)
              ? 'A review that should never have been published costs a reader nothing when it goes.'
              : 'Your star rating is the one thing on your page a traveller has to be able to trust, so a review you simply disagree with stays in the maths.'}
          </p>
        </div>

        <label className="block mt-5">
          <span className="block text-sm font-bold text-slate-900">Note (optional)</span>
          <span className="block text-xs text-slate-500 mt-0.5 mb-2">
            Only your team sees this. It is never shown to the guest.
          </span>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            rows={2}
            className="w-full text-sm text-slate-900 placeholder-slate-400 border border-solid border-slate-300 rounded-xl p-3 focus:outline-none focus:border-indigo-500"
            placeholder="Why you took this down"
          />
        </label>

        <div className="flex gap-3 mt-5">
          <button
            type="button"
            disabled={busy}
            onClick={() => onConfirm(review.id, reason, note)}
            className="bg-indigo-600 hover:bg-indigo-700 active:translate-y-px disabled:opacity-60 text-white text-sm font-bold border-0 rounded-xl px-4 py-2.5 cursor-pointer transition-colors">
            {busy ? 'Applying' : removesFromRating(reason) ? 'Remove review' : 'Hide review'}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="bg-white hover:bg-slate-50 text-slate-700 text-sm font-bold border border-solid border-slate-300 rounded-xl px-4 py-2.5 cursor-pointer transition-colors">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

ModerationDialog.propTypes = {
  review: PropTypes.object.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  busy: PropTypes.bool
};

const ReviewCard = ({
  review,
  isEditing,
  draft,
  onDraftChange,
  onStartReply,
  onCancelReply,
  onPublish,
  onAiDraft,
  onDeleteReply,
  onModerate,
  onRestore,
  busy,
  aiBusy,
  error
}) => {
  const tooLong = draft.length > MAX_REPLY_LENGTH;

  return (
    <article className={CARD}>
      <div className="flex flex-wrap items-center gap-3">
        <Stars value={review.star} />
        <span className="text-sm font-bold text-slate-900">
          {review.customerName || 'Anonymous guest'}
        </span>
        <span className="text-xs text-slate-400 font-semibold">{formatDate(review.createdAt)}</span>
        <div className="flex flex-wrap gap-2 ml-auto">
          {/* A guest who left stars but no words gave us a RATING, not a
              review. Operators make that distinction, so the screen does too:
              it is the difference between something you can answer and
              something you can only count. */}
          {review.isPrivateFeedback && <Pill tone="amber">Private feedback</Pill>}
          {!review.reviewText && !review.isPrivateFeedback && <Pill tone="slate">Rating only</Pill>}
          {review.moderation?.status === 'removed' && <Pill tone="rose">Removed</Pill>}
          {review.moderation?.status === 'hidden' && <Pill tone="amber">Hidden</Pill>}
          {review.moderation && !review.countsTowardRating && (
            <Pill tone="rose">Not in your rating</Pill>
          )}
          {review.moderation?.status === 'hidden' && <Pill tone="slate">Still in your rating</Pill>}
          {review.reply?.draftedByAi && <Pill tone="indigo">AI drafted</Pill>}
        </div>
      </div>

      <div className="text-xs text-slate-400 font-semibold mt-1">
        {[review.tourTitle, review.guideName].filter(Boolean).join(' with ') || 'Tour unknown'}
      </div>

      {/* Card 411. The low-star form tells the guest "Your comments go directly
          to the team", so these words are shown here and nowhere else: never on
          the website, never replied to in public. Only the stars count. */}
      {review.isPrivateFeedback ? (
        <div className="mt-4 max-w-[68ch] bg-amber-50 rounded-xl p-3 border border-solid border-amber-200">
          {review.privateFeedback && (
            <p className="text-sm text-slate-700 leading-relaxed m-0 whitespace-pre-wrap">
              {review.privateFeedback}
            </p>
          )}
          <p className={`text-xs text-amber-800 font-semibold m-0 ${review.privateFeedback ? 'mt-2' : ''}`}>
            Sent privately to your team after a low rating. Never published; only the stars count in your
            rating.
          </p>
        </div>
      ) : review.reviewText ? (
        <p className="text-sm text-slate-700 leading-relaxed m-0 mt-4 max-w-[68ch] whitespace-pre-wrap">
          {review.reviewText}
        </p>
      ) : (
        <p className="text-sm text-slate-400 italic m-0 mt-4">
          This guest left stars but no words, so there is nothing to reply to.
        </p>
      )}

      {review.moderation?.note && (
        <div className="mt-4 text-xs text-slate-500 bg-slate-50 rounded-xl p-3 border border-solid border-slate-200">
          <span className="font-bold text-slate-700">Your note: </span>
          {review.moderation.note}
          {review.moderation.moderatedByName && (
            <span className="text-slate-400"> ({review.moderation.moderatedByName})</span>
          )}
        </div>
      )}

      {review.reply && !isEditing && (
        <div className="mt-4 pl-4 border-0 border-l-2 border-solid border-indigo-200">
          <div className="text-xs font-bold text-indigo-700">
            {review.reply.authorName} replied {formatDate(review.reply.createdAt)}
          </div>
          <p className="text-sm text-slate-700 leading-relaxed m-0 mt-1 max-w-[68ch] whitespace-pre-wrap">
            {review.reply.body}
          </p>
        </div>
      )}

      {isEditing && (
        <div className="mt-4">
          <label className="block">
            <span className="block text-sm font-bold text-slate-900">Your public reply</span>
            <span className="block text-xs text-slate-500 mt-0.5 mb-2">
              This appears under the review on your public profile, signed with your name.
            </span>
            <textarea
              value={draft}
              onChange={e => onDraftChange(e.target.value)}
              rows={4}
              autoFocus
              className={`w-full text-sm text-slate-900 placeholder-slate-400 border border-solid rounded-xl p-3 focus:outline-none ${
                tooLong ? 'border-rose-400 focus:border-rose-500' : 'border-slate-300 focus:border-indigo-500'
              }`}
              placeholder="Answer what this guest actually said."
            />
          </label>

          <div className="flex items-center justify-between mt-1">
            <span className={`text-xs font-semibold ${tooLong ? 'text-rose-600' : 'text-slate-400'}`}>
              {draft.length} / {MAX_REPLY_LENGTH}
            </span>
          </div>

          {error && (
            <p className="text-sm text-rose-700 bg-rose-50 border border-solid border-rose-200 rounded-xl p-3 m-0 mt-3">
              {error}
            </p>
          )}

          <div className="flex flex-wrap gap-3 mt-4">
            <button
              type="button"
              disabled={busy || tooLong || !draft.trim()}
              onClick={() => onPublish(review.id)}
              className="bg-indigo-600 hover:bg-indigo-700 active:translate-y-px disabled:opacity-60 text-white text-sm font-bold border-0 rounded-xl px-4 py-2.5 cursor-pointer transition-colors">
              {busy ? 'Publishing' : review.reply ? 'Save reply' : 'Publish reply'}
            </button>
            <button
              type="button"
              disabled={aiBusy || !review.reviewText}
              onClick={() => onAiDraft(review.id)}
              title={
                review.reviewText
                  ? 'Write a first draft you can edit'
                  : 'There is no review text to draft from'
              }
              className="bg-white hover:bg-slate-50 disabled:opacity-60 text-indigo-700 text-sm font-bold border border-solid border-indigo-200 rounded-xl px-4 py-2.5 cursor-pointer transition-colors">
              {aiBusy ? 'Drafting' : 'Draft with AI'}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={onCancelReply}
              className="bg-white hover:bg-slate-50 text-slate-700 text-sm font-bold border border-solid border-slate-300 rounded-xl px-4 py-2.5 cursor-pointer transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}

      {!isEditing && (
        <div className="flex flex-wrap gap-3 mt-5">
          {review.reviewText && (
            <button
              type="button"
              onClick={() => onStartReply(review)}
              className="bg-white hover:bg-slate-50 text-slate-700 text-sm font-bold border border-solid border-slate-300 rounded-xl px-4 py-2.5 cursor-pointer transition-colors">
              {review.reply ? 'Edit reply' : 'Reply'}
            </button>
          )}
          {review.reply && (
            <button
              type="button"
              disabled={busy}
              onClick={() => onDeleteReply(review.id)}
              className="bg-transparent hover:bg-slate-50 text-slate-500 text-sm font-bold border-0 rounded-xl px-3 py-2.5 cursor-pointer transition-colors">
              Delete reply
            </button>
          )}
          {/* Only rendered when the server says this is actually published.
              A link the directory would withhold is worse than no link. */}
          {review.directoryUrl && (
            <a
              href={review.directoryUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-500 hover:text-indigo-700 text-sm font-bold no-underline px-3 py-2.5 transition-colors">
              {review.reviewText ? 'View on directory' : 'See the ratings breakdown'}
            </a>
          )}
          {review.moderation ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => onRestore(review.id)}
              className="bg-transparent hover:bg-slate-50 text-indigo-700 text-sm font-bold border-0 rounded-xl px-3 py-2.5 cursor-pointer transition-colors ml-auto">
              Restore
            </button>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={() => onModerate(review)}
              className="bg-transparent hover:bg-slate-50 text-slate-500 text-sm font-bold border-0 rounded-xl px-3 py-2.5 cursor-pointer transition-colors ml-auto">
              Hide or remove
            </button>
          )}
        </div>
      )}
    </article>
  );
};

ReviewCard.propTypes = {
  review: PropTypes.object.isRequired,
  isEditing: PropTypes.bool,
  draft: PropTypes.string,
  onDraftChange: PropTypes.func.isRequired,
  onStartReply: PropTypes.func.isRequired,
  onCancelReply: PropTypes.func.isRequired,
  onPublish: PropTypes.func.isRequired,
  onAiDraft: PropTypes.func.isRequired,
  onDeleteReply: PropTypes.func.isRequired,
  onModerate: PropTypes.func.isRequired,
  onRestore: PropTypes.func.isRequired,
  busy: PropTypes.bool,
  aiBusy: PropTypes.bool,
  error: PropTypes.string
};

const GuestReviews = () => {
  const classes = useStyles();
  const companyId = useSelector(state => state.user?.user?.companyId);

  const [queue, setQueue] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [filter, setFilter] = useState('needsReply');

  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState('');
  const [draftedByAi, setDraftedByAi] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [aiBusyId, setAiBusyId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [moderating, setModerating] = useState(null);
  const [split, setSplit] = useState(null);
  const [splitBusy, setSplitBusy] = useState(false);
  const [splitError, setSplitError] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [alertsBusy, setAlertsBusy] = useState(false);
  const [alertsError, setAlertsError] = useState(null);
  const [profile, setProfile] = useState(null);
  const [profileBusy, setProfileBusy] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [filters, setFilters] = useState({
    stars: [],
    writtenOnly: false,
    tour: '',
    guide: '',
    search: ''
  });

  const load = useCallback(async () => {
    try {
      setLoadError(null);
      const [data, splitData, alertData, profileData] = await Promise.all([
        fetchGuestReviews(),
        fetchReviewSplit(),
        fetchReviewAlerts(),
        fetchPortalProfile()
      ]);
      setQueue(data);
      setSplit(splitData);
      setAlerts(alertData);
      setProfile(profileData);
    } catch (err) {
      setLoadError(
        err?.message || 'We could not load your reviews. Refresh the page to try again.'
      );
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startReply = review => {
    setEditingId(review.id);
    setDraft(review.reply?.body || '');
    // Editing an existing AI-drafted reply keeps the flag; a fresh reply starts
    // as hand written until the operator actually asks for a draft.
    setDraftedByAi(Boolean(review.reply?.draftedByAi));
    setActionError(null);
  };

  const cancelReply = () => {
    setEditingId(null);
    setDraft('');
    setDraftedByAi(false);
    setActionError(null);
  };

  const runAction = async (reviewId, action) => {
    setBusyId(reviewId);
    setActionError(null);
    try {
      await action();
      await load();
      return true;
    } catch (err) {
      setActionError(err?.message || 'That did not work. Please try again.');
      return false;
    } finally {
      setBusyId(null);
    }
  };

  const publish = async reviewId => {
    const ok = await runAction(reviewId, () =>
      publishReviewReply({ reviewId, body: draft, draftedByAi })
    );
    if (ok) cancelReply();
  };

  const aiDraft = async reviewId => {
    setAiBusyId(reviewId);
    setActionError(null);
    try {
      const text = await draftReviewReply(reviewId);
      setDraft(text);
      setDraftedByAi(true);
    } catch (err) {
      setActionError(err?.message || 'The draft could not be written. Write the reply yourself.');
    } finally {
      setAiBusyId(null);
    }
  };

  const persistSplit = async percentage => {
    setSplitBusy(true);
    setSplitError(null);
    try {
      setSplit(await saveReviewSplit(percentage));
    } catch (err) {
      setSplitError(err?.message || 'We could not save that. Please try again.');
    } finally {
      setSplitBusy(false);
    }
  };

  const persistAlerts = async settings => {
    setAlertsBusy(true);
    setAlertsError(null);
    try {
      setAlerts(await saveReviewAlerts(settings));
    } catch (err) {
      setAlertsError(err?.message || 'We could not save that. Please try again.');
    } finally {
      setAlertsBusy(false);
    }
  };

  const persistProfile = async next => {
    setProfileBusy(true);
    setProfileError(null);
    try {
      setProfile(await savePortalProfile(next));
    } catch (err) {
      setProfileError(err?.message || 'We could not save that. Please try again.');
    } finally {
      setProfileBusy(false);
    }
  };

  const confirmModeration = async (reviewId, reason, note) => {
    const ok = await runAction(reviewId, () => moderateReview({ reviewId, reason, note }));
    if (ok) setModerating(null);
  };

  const byState = (queue?.reviews || []).filter(review => {
    if (filter === 'all') return true;
    if (filter === 'needsReply') return review.awaitingReply;
    if (filter === 'replied') return Boolean(review.reply);
    return Boolean(review.moderation);
  });

  const visibleReviews = byState.filter(review => matchesFilters(review, filters));

  // Options come from the reviews actually present, so an operator is never
  // offered a tour or guide that would return nothing.
  const uniqueBy = key =>
    [...new Set((queue?.reviews || []).map(r => r[key]).filter(Boolean))].sort();
  const tours = uniqueBy('tourTitle');
  const guides = uniqueBy('guideName');

  const filtersActive =
    filters.stars.length > 0 ||
    filters.writtenOnly ||
    Boolean(filters.tour) ||
    Boolean(filters.guide) ||
    Boolean(filters.search.trim());

  const renderBody = () => {
    if (loadError) {
      return (
        <div className={CARD}>
          <div className="text-sm font-bold text-slate-900">We could not load your reviews</div>
          <p className="text-sm text-slate-500 leading-relaxed m-0 mt-1">{loadError}</p>
          <button
            type="button"
            onClick={load}
            className="mt-4 bg-indigo-600 hover:bg-indigo-700 active:translate-y-px text-white text-sm font-bold border-0 rounded-xl px-4 py-2.5 cursor-pointer transition-colors">
            Try again
          </button>
        </div>
      );
    }

    if (!queue) return <LoadingSkeleton />;

    const counts = queue.counts;

    return (
      <>
        {profile && (
          <PortalProfile
            profile={profile}
            onSave={persistProfile}
            busy={profileBusy}
            error={profileError}
          />
        )}

        {split && (
          <ReviewSplit
            split={split}
            onSave={persistSplit}
            busy={splitBusy}
            error={splitError}
          />
        )}

        {alerts && (
          <ReviewAlerts
            alerts={alerts}
            onSave={persistAlerts}
            busy={alertsBusy}
            error={alertsError}
          />
        )}

        {companyId && <WordPressWidget companyId={String(companyId)} />}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Needs a reply', value: counts.awaitingReply },
            { label: 'Replied', value: counts.replied },
            { label: 'Hidden', value: counts.hidden },
            { label: 'Removed', value: counts.removed }
          ].map(stat => (
            <div
              key={stat.label}
              className="bg-white rounded-2xl border border-solid border-slate-200 p-4 min-w-0">
              <div className="text-2xl font-black text-slate-900">{stat.value}</div>
              <div className="text-xs text-slate-500 font-semibold mt-0.5">{stat.label}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-5">
          {FILTERS.map(option => (
            <button
              key={option.value}
              type="button"
              onClick={() => setFilter(option.value)}
              className={`text-sm font-bold rounded-xl px-3.5 py-2 cursor-pointer border border-solid transition-colors ${
                filter === option.value
                  ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
              }`}>
              {option.label}
            </button>
          ))}
        </div>

        <ReviewFilters
          filters={filters}
          setFilters={setFilters}
          tours={tours}
          guides={guides}
          shown={visibleReviews.length}
          total={byState.length}
        />

        {queue.truncated && (
          <p className="text-sm text-amber-800 bg-amber-50 border border-solid border-amber-200 rounded-xl p-3 m-0 mb-4">
            You have more reviews than this page can load at once. The newest are shown. Tell us if
            you are seeing this, because it means we owe you paging.
          </p>
        )}

        {actionError && !editingId && (
          <p className="text-sm text-rose-700 bg-rose-50 border border-solid border-rose-200 rounded-xl p-3 m-0 mb-4">
            {actionError}
          </p>
        )}

        {visibleReviews.length === 0 ? (
          <div className={CARD}>
            <div className="text-sm font-bold text-slate-900">
              {filtersActive
                ? 'No reviews match those filters'
                : filter === 'needsReply'
                ? 'Every review has an answer'
                : 'Nothing here yet'}
            </div>
            <p className="text-sm text-slate-500 leading-relaxed m-0 mt-1">
              {filtersActive
                ? `${byState.length} ${byState.length === 1 ? 'review is' : 'reviews are'} hidden by your filters. Clear them to see everything.`
                : filter === 'needsReply'
                ? 'When a guest writes a review, it lands here waiting for your reply.'
                : 'Try another filter to see the rest of your reviews.'}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {visibleReviews.map(review => (
              <ReviewCard
                key={review.id}
                review={review}
                isEditing={editingId === review.id}
                draft={editingId === review.id ? draft : ''}
                onDraftChange={setDraft}
                onStartReply={startReply}
                onCancelReply={cancelReply}
                onPublish={publish}
                onAiDraft={aiDraft}
                onDeleteReply={id => runAction(id, () => deleteReviewReply(id))}
                onModerate={setModerating}
                onRestore={id => runAction(id, () => restoreReview(id))}
                busy={busyId === review.id}
                aiBusy={aiBusyId === review.id}
                error={editingId === review.id ? actionError : null}
              />
            ))}
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <TopNav />
      <div className={classes.container}>
        <TipDirectLeftMenu selectedTab={'GuestReviews'} />
        <div className="flex-1 px-4 py-6 md:px-8 w-full max-w-7xl mx-auto min-w-0">
          <h1 className="text-2xl font-black text-slate-900 m-0">Guest Reviews</h1>
          <p className="text-sm text-slate-500 leading-relaxed m-0 mt-1 mb-5 max-w-[68ch]">
            Every review and rating your guests left with us. Reply to them, and take down anything
            that should not be public.
          </p>

          {/* Stated on the page, not just in the code. Nobody should demo this
              to a customer thinking it is finished. */}
          <div className="bg-amber-50 border border-solid border-amber-200 rounded-2xl p-4 mb-6">
            <div className="text-sm font-bold text-amber-900">Prototype</div>
            <p className="text-xs text-amber-800 leading-relaxed m-0 mt-1 max-w-[68ch]">
              This screen is a working prototype. Your replies and moderation are saved against your
              real reviews, but nothing here is published anywhere a guest can see yet. Removing a
              review does change your TipDirect rating.
            </p>
          </div>

          {renderBody()}
        </div>
      </div>

      {moderating && (
        <ModerationDialog
          review={moderating}
          busy={busyId === moderating.id}
          onCancel={() => setModerating(null)}
          onConfirm={confirmModeration}
        />
      )}
    </>
  );
};

export default GuestReviews;
